"use client";

import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Phone,
  Mail,
  MapPin,
  Edit3,
  Save,
  X,
  Calendar,
  Shield,
  Building2,
  Home,
  Crown,
} from "lucide-react";
import { Avatar } from "@/components/ui/avatar";
import { useAppSelector } from "@/store/hooks";
import { StandardFormInput } from "@/components/common/StandardFormInput";
import { Badge } from "@/components/ui/badge";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { getUserProfile, updateUserProfile } from "@/services/userService";

interface AccountDetailsFormData {
  name: string;
  email: string;
  phone_number: string;
  address_line_one: string;
  address_line_two: string;
  city: string;
  state: string;
  postal_code: string;
}

const AccountDetails = () => {
  const authState = useAppSelector((state) => state.auth);
  const { userId, adminAuth } = authState;
  console.log("Auth state in AccountDetails:", {
    userId,
    adminAuth,
    authState,
  });
  const params = useParams();
  const { storeId } = params || {};
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState<AccountDetailsFormData>({
    name: "",
    email: "",
    phone_number: "",
    address_line_one: "",
    address_line_two: "",
    city: "",
    state: "",
    postal_code: "",
  });

  const [editFormData, setEditFormData] =
    useState<AccountDetailsFormData>(formData);

  const handleEdit = () => {
    setEditFormData(formData);
    setIsEditing(true);
  };

  const handleSave = async () => {
    const userIdToUse = userId || adminAuth?.userId;
    if (!storeId || !userIdToUse) return;

    setSaving(true);
    try {
      const updatePayload = {
        name: editFormData.name,
        email: editFormData.email,
        address: {
          address_line_one: editFormData.address_line_one,
          address_line_two: editFormData.address_line_two,
          city: editFormData.city,
          state: editFormData.state,
          postal_code: editFormData.postal_code,
        },
      };
      const updatedUser = await updateUserProfile(
        storeId as string,
        userIdToUse,
        updatePayload,
      );
      setFormData({
        name: updatedUser.name || "",
        email: updatedUser.email || "",
        phone_number: updatedUser.phone_number || "",
        address_line_one: updatedUser.address?.address_line_one || "",
        address_line_two: updatedUser.address?.address_line_two || "",
        city: updatedUser.address?.city || "",
        state: updatedUser.address?.state || "",
        postal_code: updatedUser.address?.postal_code || "",
      });
      setIsEditing(false);
      toast.success("Account details updated successfully");
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to update account details",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditFormData(formData);
    setIsEditing(false);
  };

  const handleInputChange = (
    field: keyof AccountDetailsFormData,
    value: string,
  ) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Fetch user profile data on mount
  useEffect(() => {
    const fetchUserProfile = async () => {
      const userIdToUse = userId || adminAuth?.userId;
      if (!storeId || !userIdToUse) {
        console.log("Missing storeId or userId:", {
          storeId,
          userId,
          adminAuthUserId: adminAuth?.userId,
        });
        return;
      }

      // Only fetch if we don't have data
      if (formData.name || formData.email) {
        console.log("User profile already loaded, skipping fetch");
        return;
      }

      console.log("Fetching user profile:", { storeId, userIdToUse });
      setLoading(true);
      try {
        const userProfile = await getUserProfile(
          storeId as string,
          userIdToUse,
        );
        const userData = {
          name: userProfile.name || "",
          email: userProfile.email || "",
          phone_number: userProfile.phone_number || "",
          address_line_one: userProfile.address?.address_line_one || "",
          address_line_two: userProfile.address?.address_line_two || "",
          city: userProfile.address?.city || "",
          state: userProfile.address?.state || "",
          postal_code: userProfile.address?.postal_code || "",
        };
        setFormData(userData);
        setEditFormData(userData);
      } catch (error) {
        toast.error(
          error instanceof Error
            ? error.message
            : "Failed to load user profile",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [storeId, userId, adminAuth?.userId]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_3fr] gap-4">
        <div className="animate-pulse">
          <Card>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="h-12 bg-muted rounded-full"></div>
                <div className="h-4 bg-muted rounded w-3/4"></div>
                <div className="h-4 bg-muted rounded w-1/2"></div>
              </div>
            </CardContent>
          </Card>
        </div>
        <div className="animate-pulse">
          <Card>
            <CardHeader>
              <div className="h-6 bg-muted rounded w-1/3"></div>
              <div className="h-4 bg-muted rounded w-2/3"></div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="h-10 bg-muted rounded"></div>
                <div className="h-10 bg-muted rounded"></div>
                <div className="h-10 bg-muted rounded"></div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_3fr] gap-4">
      {/* Customer Info Card - Left Column */}
      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            {/* Customer Profile */}
            <div className="flex items-center gap-3">
              <Avatar
                name={formData.name || formData.email}
                className="h-12 w-12"
              />
              <div className="flex-1">
                <h3 className="font-medium text-foreground">{formData.name}</h3>
                <p className="text-sm text-muted-foreground">
                  {formData.email}
                </p>
              </div>
              <Badge
                variant="secondary"
                className="bg-green-500/20 text-green-600 hover:bg-green-500/20 border-green-200 dark:border-green-800"
              >
                Active
              </Badge>
            </div>

            {/* Contact Information Section */}
            <div>
              <h4 className="text-sm font-medium text-primary mb-3 uppercase tracking-wider">
                Contact Information
              </h4>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 bg-blue-500/10 rounded-lg flex items-center justify-center">
                    <Phone className="h-4 w-4 text-blue-600" />
                  </div>
                  <span className="text-sm text-foreground">
                    {formData.phone_number}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 bg-purple-500/10 rounded-lg flex items-center justify-center">
                    <Mail className="h-4 w-4 text-purple-600" />
                  </div>
                  <span className="text-sm text-foreground">
                    {formData.email}
                  </span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="h-8 w-8 bg-emerald-500/10 rounded-lg flex items-center justify-center mt-0.5">
                    <MapPin className="h-4 w-4 text-emerald-600" />
                  </div>
                  <div className="text-sm text-foreground leading-relaxed">
                    {formData.address_line_one}
                    {formData.address_line_two && (
                      <>, {formData.address_line_two}</>
                    )}
                    <br />
                    {formData.state && `${formData.city},  ${formData.state} - ${formData.postal_code}`}
                  </div>
                </div>
              </div>
            </div>
            {/* Account Information Section */}
            <div>
              <h4 className="text-sm font-medium text-primary mb-3 uppercase tracking-wider">
                Account Information
              </h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 bg-orange-500/10 rounded-lg flex items-center justify-center">
                      <Calendar className="h-4 w-4 text-orange-600" />
                    </div>
                    <span className="text-sm text-muted-foreground">
                      Joined
                    </span>
                  </div>
                  <Badge
                    variant="outline"
                    className="bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950 dark:text-orange-300 dark:border-orange-800"
                  >
                    Jan 15, 2024
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 bg-indigo-500/10 rounded-lg flex items-center justify-center">
                      <Shield className="h-4 w-4 text-indigo-600" />
                    </div>
                    <span className="text-sm text-muted-foreground">Role</span>
                  </div>
                  <Badge
                    variant="outline"
                    className="bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950 dark:text-indigo-300 dark:border-indigo-800"
                  >
                    Store Admin
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 bg-cyan-500/10 rounded-lg flex items-center justify-center">
                      <Building2 className="h-4 w-4 text-cyan-600" />
                    </div>
                    <span className="text-sm text-muted-foreground">
                      Active Businesses
                    </span>
                  </div>
                  <Badge
                    variant="outline"
                    className="bg-cyan-50 text-cyan-700 border-cyan-200 dark:bg-cyan-950 dark:text-cyan-300 dark:border-cyan-800"
                  >
                    3
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 bg-rose-500/10 rounded-lg flex items-center justify-center">
                      <Home className="h-4 w-4 text-rose-600" />
                    </div>
                    <span className="text-sm text-muted-foreground">
                      Addresses
                    </span>
                  </div>
                  <Badge
                    variant="outline"
                    className="bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950 dark:text-rose-300 dark:border-rose-800"
                  >
                    2
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 bg-amber-500/10 rounded-lg flex items-center justify-center">
                      <Crown className="h-4 w-4 text-amber-600" />
                    </div>
                    <span className="text-sm text-muted-foreground">
                      Subscription
                    </span>
                  </div>
                  <Badge
                    variant="outline"
                    className="bg-gradient-to-r from-amber-50 to-yellow-50 text-amber-700 border-amber-200 dark:from-amber-950 dark:to-yellow-950 dark:text-amber-300 dark:border-amber-800 font-medium"
                  >
                    ✨ Professional
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Account Details Form - Right Column */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Account Details</CardTitle>
            <CardDescription>
              Manage your account information and preferences
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <Button
                  onClick={handleSave}
                  size="sm"
                  disabled={saving}
                  className="flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {saving ? "Saving..." : "Save"}
                </Button>
                <Button
                  onClick={handleCancel}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Cancel
                </Button>
              </>
            ) : (
              <Button
                onClick={handleEdit}
                size="sm"
                className="flex items-center gap-2"
              >
                <Edit3 className="w-4 h-4" />
                Edit
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Full Name */}
            <StandardFormInput
              label="Full Name"
              value={isEditing ? editFormData.name : formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              disabled={!isEditing}
              placeholder="Enter full name"
            />

            {/* Email */}
            <StandardFormInput
              label="Email"
              type="email"
              value={isEditing ? editFormData.email : formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              disabled={!isEditing}
              placeholder="Enter email address"
            />

            {/* Mobile No */}
            <StandardFormInput
              label="Mobile No"
              value={
                isEditing ? editFormData.phone_number : formData.phone_number
              }
              onChange={(e) =>
                handleInputChange("phone_number", e.target.value)
              }
              disabled={!isEditing}
              placeholder="Enter mobile number"
            />

            {/* Zip/Postal Code */}
            <StandardFormInput
              label="Zip/Postal Code"
              value={
                isEditing ? editFormData.postal_code : formData.postal_code
              }
              onChange={(e) => handleInputChange("postal_code", e.target.value)}
              disabled={!isEditing}
              placeholder="Enter zip/postal code"
            />
          </div>

          {/* Address Line One */}
          <StandardFormInput
            label="Address Line 1"
            value={
              isEditing
                ? editFormData.address_line_one
                : formData.address_line_one
            }
            onChange={(e) =>
              handleInputChange("address_line_one", e.target.value)
            }
            disabled={!isEditing}
            placeholder="Enter address line 1"
          />

          {/* Address Line Two */}
          <StandardFormInput
            label="Address Line 2"
            value={
              isEditing
                ? editFormData.address_line_two
                : formData.address_line_two
            }
            onChange={(e) =>
              handleInputChange("address_line_two", e.target.value)
            }
            disabled={!isEditing}
            placeholder="Enter address line 2 (optional)"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* City */}
            <StandardFormInput
              label="City"
              value={isEditing ? editFormData.city : formData.city}
              onChange={(e) => handleInputChange("city", e.target.value)}
              disabled={!isEditing}
              placeholder="Enter city"
            />

            {/* State */}
            <StandardFormInput
              label="State"
              value={isEditing ? editFormData.state : formData.state}
              onChange={(e) => handleInputChange("state", e.target.value)}
              disabled={!isEditing}
              placeholder="Enter state"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AccountDetails;
